// eslint-disable-next-line max-classes-per-file
import { format } from 'date-fns';
import { AxiosResponse } from 'axios';

import { instance as axios } from 'api/axios';
import { trims } from 'utils';
import { PhoneService } from '../Phone/service';
import { Visitor } from '../Visitor/interfaces';
import { TripSecurityTable } from '../../modules/security/global/interfaces';
import { TripOrderingTable } from '../../modules/ordering/global/interfaces';
import { OrderService } from '../Order/service';
import { clientDateFormat } from './constants';
import { NewTrip, Trip } from './interfaces';

class MyError extends Error {
  errorExtraParamsGet;

  constructor(message: any, errorExtraParams: any) {
    super(message);
    this.errorExtraParamsGet = errorExtraParams;
  }

  get errorExtraParams() {
    return this.errorExtraParamsGet;
  }
}

export class TripService {
  public static getVisitorPhoneNumber(visitor: Visitor | undefined): string {
    if (!visitor || !visitor.phone) {
      return '';
    }
    return PhoneService.formatPhone(visitor.phone.number);
  }

  static prefix = '/Trip';

  public static async getUnloadingTrips() {
    const branchLocal = localStorage.getItem('branchId') || 'null';
    const branchId = JSON.parse(branchLocal)?.id;

    if (branchId) {
      try {
        const url = `${TripService.prefix}?branchId=${branchId}&unloading=true`;
        const response: AxiosResponse<Trip[]> = await axios.get(url);

        return response.data;
      } catch (error: any) {
        throw new Error(error);
      }
    } else {
      throw new Error('Виберіть філію');
    }
  }

  public static async getTrips() {
    const branchLocal = localStorage.getItem('branchId') || 'null';
    const branchId = JSON.parse(branchLocal)?.id;

    if (branchId) {
      try {
        const url = `${TripService.prefix}?branchId=${branchId}`;
        const response: AxiosResponse<Trip[]> = await axios.get(url);

        return response.data;
      } catch (error: any) {
        throw new Error(error);
      }
    } else {
      throw new Error('Виберіть філію');
    }
  }

  public static async getNewTrip(data: FormData) {
    try {
      const url = `${TripService.prefix}/new`;
      const response: AxiosResponse<NewTrip> = await axios.post(url, data);

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.UserMessage || 'Виникла помилка');
    }
  }

  public static async getTrip(props: any) {
    const { id, params } = props;
    try {
      const url = `${TripService.prefix}/${id}`;
      const response: AxiosResponse<Trip> = await axios.get(url, params);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.UserMessage || 'Виникла помилка');
    }
  }

  public static async saveTrip(id: number, data: FormData) {
    try {
      const url = `${TripService.prefix}`;
      const response: AxiosResponse<Trip> = await axios.put(url, data);
      return response.data;
    } catch (error: any) {
      throw new MyError(error, error.response);
    }
  }

  public static async updateTrip(id: number, data: FormData) {
    try {
      const url = `${TripService.prefix}/NextStep`;
      const response: AxiosResponse<Trip> = await axios.put(url, data);
      return response.data;
    } catch (error: any) {
      throw new MyError(error, error.response);
    }
  }

  public static async updateTripStatus(data: FormData) {
    try {
      const url = `${TripService.prefix}/UpdateStatus`;
      const response: AxiosResponse<Trip> = await axios.put(url, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.UserMessage || 'Виникла помилка');
    }
  }

  public static async postTrip(data: FormData) {
    try {
      const url = `${TripService.prefix}`;
      const response: AxiosResponse<Trip> = await axios.put(url, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.UserMessage || 'Виникла помилка');
    }
  }

  public static async deleteTrip(id: number) {
    try {
      const url = `${TripService.prefix}/${id}`;
      const response: AxiosResponse<any> = await axios.delete(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.UserMessage || 'Виникла помилка');
    }
  }

  public static async nextStep(body: any) {
    try {
      const url = `${TripService.prefix}/NextStep`;
      const response: AxiosResponse<any> = await axios.put(url, body);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.UserMessage || 'Виникла помилка');
    }
  }

  public static formatTripsForSecurityTable(trips: Trip[]): TripSecurityTable[] {
    return trips.map(
      (trip): TripSecurityTable => ({
        id: trip.id,
        key: trip.id,
        сarrierTypeStr: trip.сarrierTypeStr,
        status: trip.status,
        statusId: trip.statusId,
        statusStr: trip.status.title,
        bookingDate: trip?.bookingDate ? format(new Date(trip?.bookingDate), clientDateFormat) : '',
        arrivalDate: trip?.arrivalDate ? format(new Date(trip?.arrivalDate), clientDateFormat) : '',
        vehicleLicensePlate: (() => {
          if (trip.vehicle?.licensePlate) return trip.vehicle?.licensePlate;
          return '';
        })(),
        trailerLicensePlate: (() => {
          if (trip.trailer?.licensePlate) return trip.trailer?.licensePlate;
          return '';
        })(),
        driver: (() => {
          if (trip.driver) {
            let firstName = '';
            let middleName = '';
            let lastName = '';
            if (trip.driver.firstName) firstName = trip.driver.firstName;
            if (trip.driver.middleName) middleName = trip.driver.middleName;
            if (trip.driver.lastName) lastName = trip.driver.lastName;

            return `${lastName} ${firstName} ${middleName}`;
          }
          return '';
        })(),
        driverPhone: TripService.getVisitorPhoneNumber(trip.driver),
        forwarder: (() => {
          if (trip.forwarder) {
            let firstName = '';
            let middleName = '';
            let lastName = '';
            if (trip.forwarder.firstName) firstName = trip.forwarder.firstName;
            if (trip.forwarder.middleName) middleName = trip.forwarder.middleName;
            if (trip.forwarder.lastName) lastName = trip.forwarder.lastName;

            return `${lastName} ${firstName} ${middleName}`;
          }
          return '';
        })(),
        forwarderPhone: TripService.getVisitorPhoneNumber(trip.forwarder),
        departureSecurityGate: trims(trip?.departureSecurityGate?.name || ''),

        orders: OrderService.getOrderNumbersString(trip.orders),
        factoryNumber: OrderService.getOrderFactoryNumbers(trip.orders),
        warehouseCode: OrderService.getOrderWarehouseCodes(trip.orders),
        orderItemsCount: trip.orderItemsCount,
        unloadingFactory: trip.unloadingLocationConfiguration?.split('; ')[2],
        unloadingWarehouse: trip.unloadingLocationConfiguration?.split('; ')[1],
        unloadingLocation: trip.unloadingLocationConfiguration?.split('; ')[0],
        factWeightGrouped: OrderService.getFactWeightGrouped(trip.orders),
      }),
    );
  }

  public static formatTripsForOrderingTable(trips: Trip[]): TripOrderingTable[] {
    return trips.map(
      (trip): TripOrderingTable => ({
        id: trip.id,
        key: trip.id,
        сarrierTypeStr: trip.сarrierTypeStr,
        bookingDate: trip?.bookingDate ? format(new Date(trip?.bookingDate), clientDateFormat) : '',
        arrivalDate: trip?.arrivalDate ? format(new Date(trip?.arrivalDate), clientDateFormat) : '',
        driver: (() => {
          if (trip.driver) {
            let firstName = '';
            let middleName = '';
            let lastName = '';
            if (trip.driver.firstName) firstName = trip.driver.firstName;
            if (trip.driver.middleName) middleName = trip.driver.middleName;
            if (trip.driver.lastName) lastName = trip.driver.lastName;

            return `${lastName} ${firstName} ${middleName}`;
          }
          return '';
        })(),
        driverPhone: TripService.getVisitorPhoneNumber(trip.driver),
        isFastLineDriver: trip.isFastLineDriver,
        orders: OrderService.getOrderNumbersString(trip.orders),
        forwarder: (() => {
          if (trip.forwarder) {
            let firstName = '';
            let middleName = '';
            let lastName = '';
            if (trip.forwarder.firstName) firstName = trip.forwarder.firstName;
            if (trip.forwarder.middleName) middleName = trip.forwarder.middleName;
            if (trip.forwarder.lastName) lastName = trip.forwarder.lastName;

            return `${lastName} ${firstName} ${middleName}`;
          }
          return '';
        })(),
        forwarderPhone: TripService.getVisitorPhoneNumber(trip.forwarder),
        status: trip.status,
        statusId: trip.statusId,
        statusStr: trip.status.title,
        factoryNumber: OrderService.getOrderFactoryNumbers(trip.orders),
        warehouseCode: OrderService.getOrderWarehouseCodes(trip.orders),
        orderItemsCount: trip.orderItemsCount,
        unloadingFactory: trip.unloadingLocationConfiguration?.split('; ')[2],
        unloadingWarehouse: trip.unloadingLocationConfiguration?.split('; ')[1],
        unloadingLocation: trip.unloadingLocationConfiguration?.split('; ')[0],
        factWeightGrouped: OrderService.getFactWeightGrouped(trip.orders),
      }),
    );
  }
}
